import { Component, Input, Output, EventEmitter, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppIconComponent } from './app-icon.component';
import { AppButtonComponent } from './app-button.component';

export type NotificationType = 'success' | 'warning' | 'error' | 'info';

/**
 * AppNotificationComponent - Microcomponente para notificações/snackbar
 * Substitui mat-snackbar com implementação em Tailwind CSS
 *
 * @example
 * <app-notification
 *   type="success"
 *   message="Operação realizada com sucesso!"
 *   [showAction]="true"
 *   actionText="Desfazer"
 *   (actionClick)="undoAction()"
 *   (close)="closeNotification()"
 * />
 */
@Component({
  selector: 'app-notification',
  standalone: true,
  imports: [CommonModule, AppIconComponent, AppButtonComponent],
  template: `
    <div
      [class]="getNotificationClasses()"
      role="alert"
      [attr.aria-live]="type() === 'error' ? 'assertive' : 'polite'"
    >
      <!-- Icon -->
      <div class="flex-shrink-0">
        <app-icon [name]="getIcon()" [size]="20" [class]="getIconClasses()" />
      </div>

      <!-- Content -->
      <div class="flex-1 ml-3">
        <p [class]="getMessageClasses()">{{ message() }}</p>
        @if (description()) {
        <p [class]="getDescriptionClasses()">{{ description() }}</p>
        }
      </div>

      <!-- Actions -->
      <div class="flex items-center ml-4 space-x-2">
        @if (showAction() && actionText()) {
        <app-button
          [variant]="'ghost'"
          [class]="getActionButtonClasses()"
          (clicked)="onActionClick()"
        >
          {{ actionText() }}
        </app-button>
        } @if (showClose()) {
        <button
          type="button"
          [class]="getCloseButtonClasses()"
          (click)="onClose()"
          aria-label="Fechar notificação"
        >
          <app-icon name="close" [size]="18" />
        </button>
        }
      </div>
    </div>
  `,
  styles: [],
})
export class AppNotificationComponent {
  @Input() type = signal<NotificationType>('info');
  @Input() message = signal('');
  @Input() description = signal('');
  @Input() showAction = signal(false);
  @Input() actionText = signal('');
  @Input() showClose = signal(true);
  @Input() duration = signal(5000); // Auto-close duration in ms

  @Output() actionClick = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  getNotificationClasses(): string {
    const baseClasses =
      'flex items-start p-4 rounded-lg shadow-md border-l-4 transition-all duration-200';

    switch (this.type()) {
      case 'success':
        return `${baseClasses} bg-green-50 border-green-500`;
      case 'warning':
        return `${baseClasses} bg-yellow-50 border-yellow-500`;
      case 'error':
        return `${baseClasses} bg-red-50 border-red-500`;
      case 'info':
      default:
        return `${baseClasses} bg-blue-50 border-blue-500`;
    }
  }

  getIcon(): string {
    switch (this.type()) {
      case 'success':
        return 'check_circle';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'info':
      default:
        return 'info';
    }
  }

  getIconClasses(): string {
    switch (this.type()) {
      case 'success':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      case 'info':
      default:
        return 'text-blue-600';
    }
  }

  getMessageClasses(): string {
    switch (this.type()) {
      case 'success':
        return 'text-sm font-medium text-green-800';
      case 'warning':
        return 'text-sm font-medium text-yellow-800';
      case 'error':
        return 'text-sm font-medium text-red-800';
      case 'info':
      default:
        return 'text-sm font-medium text-blue-800';
    }
  }

  getDescriptionClasses(): string {
    switch (this.type()) {
      case 'success':
        return 'text-sm text-green-700 mt-1';
      case 'warning':
        return 'text-sm text-yellow-700 mt-1';
      case 'error':
        return 'text-sm text-red-700 mt-1';
      case 'info':
      default:
        return 'text-sm text-blue-700 mt-1';
    }
  }

  getActionButtonClasses(): string {
    switch (this.type()) {
      case 'success':
        return 'text-green-800 hover:bg-green-100';
      case 'warning':
        return 'text-yellow-800 hover:bg-yellow-100';
      case 'error':
        return 'text-red-800 hover:bg-red-100';
      case 'info':
      default:
        return 'text-blue-800 hover:bg-blue-100';
    }
  }

  getCloseButtonClasses(): string {
    const baseClasses =
      'p-1 rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2';

    switch (this.type()) {
      case 'success':
        return `${baseClasses} text-green-600 hover:bg-green-100 focus:ring-green-500`;
      case 'warning':
        return `${baseClasses} text-yellow-600 hover:bg-yellow-100 focus:ring-yellow-500`;
      case 'error':
        return `${baseClasses} text-red-600 hover:bg-red-100 focus:ring-red-500`;
      case 'info':
      default:
        return `${baseClasses} text-blue-600 hover:bg-blue-100 focus:ring-blue-500`;
    }
  }

  onActionClick(): void {
    this.actionClick.emit();
  }

  onClose(): void {
    this.close.emit();
  }
}
